import ActionButtonWithDropdownItems from '@/components/ActionButtonWithDropdownItems';
import Constants from '@/Constants';
import { OrderBy } from '@/models/OrderBy';
import { PaginationData } from '@/models/PaginationData';
import { PersonRequest } from '@/models/Person';
import { getAuth } from '@/services/IdentityService';
import { getHost } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import { getPeopleClear, getPeopleRequest, getTotalPeopleRequest } from '@/store/person/action';
import React, { FormEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PeopleMassImport from '../PeopleMassImport';

interface PeopleSearchProps {
    handleNewPerson: () => void;
    paginationData: PaginationData;
}

const PeopleSearch = ({ handleNewPerson, paginationData }: PeopleSearchProps) => {
    const dispatch = useDispatch();
    const {
        data: {
            user: { orgId, applicationId },
        },
    } = useSelector((state: ApplicationState) => state.identity);
    const { data: strings } = useSelector((state: ApplicationState) => state.locale);
    const alphabeticalOrder: OrderBy = { description: strings.ALPHABETICAL_ORDER, value: Constants.USER_ORDER_BY_NAME };
    const newlyCreatedOrder: OrderBy = { description: strings.NEWLY_ADDED, value: Constants.USER_ORDER_BY_NEWS };

    const { totalActives } = useSelector((state: ApplicationState) => state.people);
    const { totalLicenses } = useSelector((state: ApplicationState) => state.appOrganization.data);

    const [visibility, setVisibility] = useState({ active: false, inactive: false });
    const [orderBy, setOrderBy] = useState<OrderBy>(alphabeticalOrder);
    const [search, setSearch] = useState('');

    const token = getAuth();

    useEffect(() => {
        dispatchGetTotalActivePeopleRequest();
    }, []);

    useEffect(() => {
        dispatchPeopleRequest(visibility, orgId, applicationId, orderBy, search);
    }, [dispatch, orderBy, search, visibility.active, visibility.inactive, paginationData]);

    const orderByHandle = (order: OrderBy) => {
        setOrderBy((prevState) => {
            if (prevState.description !== order.description) {
                dispatch(getPeopleClear());
                paginationData.offset = 0;
            }

            return order;
        });
    };

    const searchTermHandle = (searchTerm: string) => {
        setSearch((prevState) => {
            if (prevState !== searchTerm) {
                dispatch(getPeopleClear());
                paginationData.offset = 0;
            }

            return searchTerm;
        });
    };

    const visibilityHandle = (type: string) => {
        switch (type) {
            case strings.ACTIVE:
                setVisibility((prevState) => {
                    dispatch(getPeopleClear());
                    paginationData.offset = 0;
                    return { ...prevState, active: !prevState.active };
                });
                break;

            case strings.INACTIVE:
                setVisibility((prevState) => {
                    dispatch(getPeopleClear());
                    paginationData.offset = 0;
                    return { ...prevState, inactive: !prevState.inactive };
                });
                break;
        }
    };

    const dispatchPeopleRequest = (
        visibility: { active: boolean; inactive: boolean },
        orgId: number,
        applicationId: number,
        orderBy: OrderBy,
        search: string,
    ) => {
        const onlyActive =
            (visibility.active && visibility.inactive) || (!visibility.active && !visibility.inactive) ? undefined : visibility.active ? true : false;
        const searchRequest: PersonRequest = {
            offset: paginationData.offset,
            limit: paginationData.limit,
            orgId,
            applicationId,
            orderBy: orderBy.value,
            onlyActive,
            search,
        };
        dispatch(getPeopleRequest(searchRequest));
    };

    const getTemplateURI = () => {
        return `${getHost()}/rest/person/imports/applications/${applicationId}/organizations/${orgId}/template`;
    };

    const getPeopleURI = () => {
        return `${getHost()}/rest/tracking/people/applications/${applicationId}/organizations/${orgId}?token=${token}`;
    };

    const dispatchGetTotalActivePeopleRequest = () => {
        dispatch(
            getTotalPeopleRequest({
                offset: 0,
                limit: 0,
                orgId,
                applicationId,
                orderBy: orderBy.value,
                onlyActive: true,
                search: '',
            } as PersonRequest),
        );
    };

    return (
        <>
            <div className="d-flex top-content">
                <ActionButtonWithDropdownItems
                    actionButtonClick={handleNewPerson}
                    actionButtonLabel={strings.NEW_PEOPLE}
                    disabled={totalLicenses !== null && totalActives >= totalLicenses}
                    dropdownLabel={strings.massImport.MASS_ADD}
                >
                    <PeopleMassImport
                        onChange={() => {
                            dispatchPeopleRequest(visibility, orgId, applicationId, orderBy, search);
                        }}
                    />
                    <a className="dropdown-item pt-3 pb-3" href={getTemplateURI()}>
                        <i className="material-icons-outlined va-t mr-1">save_alt</i>
                        {strings.massImport.TEMPLATE}
                    </a>
                    <a className="dropdown-item pt-3 pb-3" href={getPeopleURI()}>
                        <i className="material-icons-outlined va-t mr-1">save_alt</i>
                        {strings.massImport.DOWNLOAD}
                    </a>
                </ActionButtonWithDropdownItems>
                <form
                    className="form-group inner-addon left-addon"
                    onSubmit={(e: FormEvent) => {
                        e.preventDefault();
                    }}
                >
                    <i className="material-icons-outlined align-middle input-icon">search</i>
                    <input
                        autoComplete="nope"
                        className="form-control"
                        id="search-input"
                        onChange={(e) => searchTermHandle(e.target.value)}
                        placeholder={strings.SEARCH}
                        type="search"
                        value={search}
                    />
                </form>
            </div>
            <>
                <div className="filters">
                    <span className="filter-text-left">{strings.FILTER_BY}:</span>
                    <span className="filter-text-right">{strings.ORDER_BY}:</span>
                </div>

                <div className="filters">
                    <div className="filters-one">
                        <div className="dropdown">
                            <button className="btn btn-primary btn-sm filter" data-toggle="dropdown" type="button">
                                {strings.VISIBILITY}
                            </button>
                            <div className="dropdown-menu dropdown-menu-right drop-three">
                                <div className="triangle-container t-three">
                                    <div className="dropdown-triangle" />
                                    <div className="dropdown-triangle-border" />
                                </div>
                                <div
                                    className="dropdown-content"
                                    onClick={() => visibilityHandle(strings.ACTIVE)}
                                    onKeyDown={() => {}}
                                    role="button"
                                    tabIndex={0}
                                >
                                    <i className="material-icons va-t m-right--xxs">
                                        {visibility.active ? Constants.icons.CHECK_BOX : Constants.icons.CHECK_BOX_OUTLINE_BLANK}
                                    </i>
                                    <span>{strings.ACTIVE}</span>
                                </div>
                                <div
                                    className="dropdown-content"
                                    onClick={() => visibilityHandle(strings.INACTIVE)}
                                    onKeyDown={() => {}}
                                    role="button"
                                    tabIndex={0}
                                >
                                    <i className="material-icons va-t m-right--xxs">
                                        {visibility.inactive ? Constants.icons.CHECK_BOX : Constants.icons.CHECK_BOX_OUTLINE_BLANK}
                                    </i>
                                    <span>{strings.INACTIVE}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="dropdown">
                        <div className="filters-two">
                            <button className="btn btn-primary btn-sm order-by" data-toggle="dropdown" type="button">
                                <span className="align-middle">{orderBy.description}</span>
                                <i className="material-icons-outlined align-middle down">arrow_drop_down</i>
                                <i className="material-icons-outlined align-middle up">arrow_drop_up</i>
                            </button>
                            <div className="dropdown-menu dropdown-menu-right drop-order">
                                <div className="triangle-container t-order">
                                    <div className="dropdown-triangle" />
                                    <div className="dropdown-triangle-border" />
                                </div>
                                <div
                                    className="dropdown-content"
                                    onClick={() => orderByHandle(alphabeticalOrder)}
                                    onKeyDown={() => {}}
                                    role="button"
                                    tabIndex={0}
                                >
                                    <span>{strings.ALPHABETICAL_ORDER}</span>
                                </div>
                                <div
                                    className="dropdown-content"
                                    onClick={() => orderByHandle(newlyCreatedOrder)}
                                    onKeyDown={() => {}}
                                    role="button"
                                    tabIndex={0}
                                >
                                    <span>{strings.NEWLY_ADDED}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        </>
    );
};

export default PeopleSearch;
